<template>
  <div id="layout-wrapper">
    <NavigationBar/>

    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page title -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Attendance Dashboard</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                    <li class="breadcrumb-item active">Leave & Attendance</li>
                    <li class="breadcrumb-item active">Monthly Attendance Report</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Data availability warning -->
          <div v-if="summary.period?.message" class="row mb-4">
            <div class="col-12">
              <div class="alert alert-warning d-flex align-items-center" role="alert">
                <i class="mdi mdi-alert-circle me-2 font-size-18"></i>
                <div>
                  <strong>Data Notice:</strong> {{ summary.period.message }}
                  <span v-if="!summary.hasAttendanceRecords">
                    You may need to import historical data or verify your system start date.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Stats cards -->
          <div class="row mb-4">
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                          <i class="mdi mdi-calendar font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Total Working Days</h6>
                      <h4 class="mb-0">{{ summary.totalWorkingDays || 0 }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                          <i class="mdi mdi-account-check font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Total Days Present</h6>
                      <h4 class="mb-0">{{ summary.totalPresent || 0 }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-danger rounded-circle text-white font-size-20">
                          <i class="mdi mdi-account-off font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Total Absences</h6>
                      <h4 class="mb-0">{{ summary.totalAbsent || 0 }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                          <i class="mdi mdi-account-heart font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Total Leave Days</h6>
                      <h4 class="mb-0">{{ summary.totalLeave || 0 }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional stats for data completeness -->
          <div class="row mb-4" v-if="summary.totalNoData > 0">
            <div class="col-12">
              <div class="card border-warning">
                <div class="card-body py-3">
                  <div class="d-flex align-items-center">
                    <i class="mdi mdi-information-outline text-warning me-3 font-size-24"></i>
                    <div>
                      <h6 class="mb-1">Data Completeness</h6>
                      <p class="mb-0 text-muted">
                        {{ summary.totalNoData }} day(s) have no attendance records.
                        This might indicate missing data or dates before system implementation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Legend -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card">
                <div class="card-body py-2">
                  <div class="d-flex flex-wrap align-items-center">
                    <span class="me-4 mb-2 mb-md-0"><strong>Legend:</strong></span>
                    <div class="d-flex flex-wrap">
                      <span class="badge bg-success me-3 mb-2">P - Present</span>
                      <span class="badge bg-danger me-3 mb-2">A - Absent</span>
                      <span class="badge bg-info me-3 mb-2">L - Leave</span>
                      <span class="badge bg-warning me-3 mb-2">WO - Weekend/Off</span>
                      <span class="badge bg-primary me-3 mb-2">WP - Weekend Present</span>
                      <span class="badge bg-secondary me-3 mb-2">WL - Weekend Leave</span>
                      <span class="badge bg-light text-dark me-3 mb-2">- - No Data</span>
                      <span class="badge bg-muted text-white me-3 mb-2">NA - Not Applicable</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Attendance table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white py-3">
                  <div class="row g-3 align-items-end">
                    <div class="col-md-3">
                      <label class="form-label">Department</label>
                      <select v-model="selectedDepartment" class="form-select" @change="getAttendanceData">
                        <option value="">All Departments</option>
                        <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                          {{ dept.departmentName }}
                        </option>
                      </select>
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Month</label>
                      <input
                          v-model="selectedMonth"
                          type="month"
                          class="form-control"
                          :max="getMaxSelectableMonth()"
                          :min="getMinSelectableMonth()"
                          @change="onMonthChange"
                      >
                      <div class="form-text">
                        <small class="text-muted">
                          Available: {{ getMinSelectableMonth() }} to {{ getMaxSelectableMonth() }}
                        </small>
                      </div>
                    </div>

                    <div class="col-md-4">
                      <label class="form-label">Search Employee</label>
                      <input
                          type="text"
                          v-model="searchValue"
                          class="form-control"
                          placeholder="Search by name or employee number..."
                          @input="onSearchInput"
                      >
                    </div>

                    <div class="col-md-2">
                      <button
                          type="button"
                          class="btn btn-primary w-100"
                          @click="exportToExcel"
                          :disabled="employees.length === 0"
                      >
                        <i class="mdi mdi-file-excel me-1"></i> Export
                      </button>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Loading state -->
                  <div v-if="loading" class="text-center p-5">
                    <div class="spinner-border text-primary me-3" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3 text-muted">Loading attendance data...</p>
                  </div>

                  <!-- Empty state -->
                  <div v-else-if="employees.length === 0" class="text-center py-5">
                    <i class="mdi mdi-account-search font-size-48 text-muted"></i>
                    <h5 class="mt-3">No employees found</h5>
                    <p class="text-muted">
                      {{ summary.period?.message || "Try adjusting your search or filters" }}
                    </p>
                  </div>

                  <!-- Attendance table -->
                  <div v-else class="table-responsive">
                    <table class="table table-bordered align-middle">
                      <thead class="table-light sticky-top">
                      <tr>
                        <th scope="col" class="bg-light position-sticky start-0" style="z-index: 101;">
                          Employee
                          <br>
                          <small class="text-muted">Join Date</small>
                        </th>
                        <th scope="col" v-for="(day, index) in days" :key="index"
                            :class="{'bg-light-warning': isWeekend(day), 'current-day': isCurrentDay(day)}">
                          {{ formatDay(day) }}
                          <span v-if="isCurrentDay(day)" class="badge rounded-pill bg-primary ms-1">Today</span>
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="employee in employees" :key="employee.employeeId" class="employee-row">
                        <td class="position-sticky start-0 bg-white" style="z-index: 99;">
                          <div>
                            <strong>{{ employee.employeeName }}</strong>
                            <br>
                            <small class="text-muted">
                              {{ employee.dateJoined ? formatJoinDate(employee.dateJoined) : 'Join date not available' }}
                            </small>
                          </div>
                        </td>
                        <td v-for="(day, index) in days" :key="index"
                            :class="getAttendanceClass(employee.attendanceByDay[day], day, employee.dateJoined)"
                            class="text-center fw-bold"
                            :title="getAttendanceTooltip(employee.attendanceByDay[day], day, employee.dateJoined)">
                          {{ getDisplayStatus(employee.attendanceByDay[day], day) }}
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div class="d-flex justify-content-between align-items-center mt-4" v-if="summary.totalPages > 1">
                    <div>
                      <span>Showing {{ employees.length }} of {{ summary.totalEmployees }} employees</span>
                    </div>
                    <nav>
                      <ul class="pagination pagination-rounded mb-0">
                        <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                          <a class="page-link" href="javascript:void(0)" @click="changePage(currentPage - 1)">
                            <i class="mdi mdi-chevron-left"></i>
                          </a>
                        </li>
                        <li v-for="page in paginationPages" :key="'page-'+page"
                            class="page-item"
                            :class="{ active: page === currentPage, disabled: page === '...' }">
                          <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                            {{ page }}
                          </a>
                          <span v-else class="page-link">{{ page }}</span>
                        </li>
                        <li class="page-item" :class="{ disabled: currentPage >= summary.totalPages }">
                          <a class="page-link" href="javascript:void(0)" @click="changePage(currentPage + 1)">
                            <i class="mdi mdi-chevron-right"></i>
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavigationBar from "@/components/Navigation.vue";
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import * as XLSX from 'xlsx';

export default {
  name: "ReportMonthly",
  components: { NavigationBar },
  data() {
    return {
      employees: [],
      days: [],
      departments: [],
      summary: {
        totalEmployees: 0,
        totalPresent: 0,
        totalAbsent: 0,
        totalLeave: 0,
        totalNoData: 0,
        totalWorkingDays: 0,
        totalPages: 1,
        hasData: false,
        hasAttendanceRecords: false,
        period: {
          month: "",
          totalDays: 0,
          message: ""
        }
      },
      searchValue: "",
      selectedDepartment: "",
      selectedMonth: this.getCurrentMonth(),
      currentPage: 1,
      pageSize: 20,
      loading: false,
      systemStartDate: "2023-01" // Adjust this to match your actual system start date
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const totalPages = this.summary.totalPages;

      if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        if (this.currentPage <= 3) {
          pages.push(2, 3);
          if (totalPages > 3) pages.push('...');
        } else if (this.currentPage >= totalPages - 2) {
          pages.push('...', totalPages - 2, totalPages - 1);
        } else {
          pages.push('...', this.currentPage - 1, this.currentPage, this.currentPage + 1, '...');
        }
        if (!pages.includes(totalPages)) {
          pages.push(totalPages);
        }
      }
      return pages;
    }
  },
  mounted() {
    this.getDepartments();
    this.getAttendanceData();
  },
  methods: {
    getCurrentMonth() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    },

    getMaxSelectableMonth() {
      return this.getCurrentMonth();
    },

    getMinSelectableMonth() {
      return this.systemStartDate;
    },

    validateSelectedMonth() {
      const selected = new Date(this.selectedMonth + '-01');
      const current = new Date();
      current.setDate(1); // Set to first day of current month

      const systemStart = new Date(this.systemStartDate + '-01');

      if (selected > current) {
        this.showToast("Cannot view attendance for future months", "warning");
        this.selectedMonth = this.getCurrentMonth();
        return false;
      }

      if (selected < systemStart) {
        this.showToast(`Data not available before ${this.formatMonthYear(this.systemStartDate)}`, "warning");
        this.selectedMonth = this.systemStartDate;
        return false;
      }

      return true;
    },

    formatMonthYear(monthString) {
      const date = new Date(monthString + '-01');
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    },

    onMonthChange() {
      if (this.validateSelectedMonth()) {
        this.currentPage = 1;
        this.getAttendanceData();
      }
    },

    formatDay(dayString) {
      const date = new Date(dayString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('default', { month: 'short' });
      const dayOfWeek = date.toLocaleString('default', { weekday: 'short' });
      return `${day} ${month} (${dayOfWeek})`;
    },

    formatJoinDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    isCurrentDay(dayString) {
      const today = new Date();
      const date = new Date(dayString);
      return today.getDate() === date.getDate() &&
          today.getMonth() === date.getMonth() &&
          today.getFullYear() === date.getFullYear();
    },

    isWeekend(dayString) {
      const date = new Date(dayString);
      const day = date.getDay();
      return day === 0 || day === 6;
    },

    wasEmployeeActiveOnDate(joinDate, checkDate) {
      if (!joinDate) return true;
      return new Date(checkDate) >= new Date(joinDate);
    },

    getAttendanceClass(status, day, joinDate) {
      const isWeekend = this.isWeekend(day);
      const wasActive = this.wasEmployeeActiveOnDate(joinDate, day);

      if (!wasActive) {
        return 'bg-secondary text-white opacity-50';
      }

      if (isWeekend) {
        switch(status) {
          case 'P':
            return 'bg-primary text-white weekend-work';
          case 'L':
            return 'bg-secondary text-white weekend-leave';
          case 'A':
            return 'bg-dark text-white weekend-absent';
          case 'WO':
          default:
            return 'bg-warning text-dark';
        }
      } else {
        switch(status) {
          case 'P':
            return 'bg-success text-white';
          case 'A':
            return 'bg-danger text-white';
          case 'L':
            return 'bg-info text-white';
          case '-':
            return 'bg-light text-muted';
          case 'NA':
            return 'bg-secondary text-white opacity-50';
          default:
            return 'bg-light text-muted';
        }
      }
    },

    getAttendanceTooltip(status, day, joinDate) {
      const isWeekend = this.isWeekend(day);
      const wasActive = this.wasEmployeeActiveOnDate(joinDate, day);

      if (!wasActive) {
        return 'Employee had not joined yet on this date';
      }

      if (isWeekend) {
        switch(status) {
          case 'P':
            return 'Present on Weekend - Outstanding Work!';
          case 'L':
            return 'Leave on Weekend';
          case 'A':
            return 'Absent on Weekend';
          case 'WO':
          default:
            return 'Weekend/Day Off';
        }
      } else {
        switch(status) {
          case 'P':
            return 'Present';
          case 'A':
            return 'Absent';
          case 'L':
            return 'Leave';
          case '-':
            return 'No attendance record available';
          case 'NA':
            return 'Employee had not joined yet';
          default:
            return status;
        }
      }
    },

    getDisplayStatus(status, day) {
      const isWeekend = this.isWeekend(day);

      if (isWeekend) {
        switch(status) {
          case 'P':
            return 'WP'; // Weekend Present
          case 'L':
            return 'WL'; // Weekend Leave
          case 'A':
            return 'WA'; // Weekend Absent
          case 'WO':
          default:
            return 'WO'; // Weekend Off
        }
      } else {
        return status || '-';
      }
    },

    onSearchInput: _.debounce(function() {
      this.currentPage = 1;
      this.getAttendanceData();
    }, 500),

    showToast(message, type = 'info') {
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback for when toast is not available
        console.log(`${type.toUpperCase()}: ${message}`);
        alert(message);
      }
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success && res.data.data?.departments) {
              this.departments = res.data.data.departments;
            }
          })
          .catch((e) => {
            console.error("Error fetching departments:", e);
            this.showToast("Failed to load departments", "error");
          });
    },

    getAttendanceData() {
      if (!this.validateSelectedMonth()) {
        return;
      }

      this.loading = true;
      const data = new FormData();
      data.append("function", "getMonthlyAttendanceDashboard");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("selectedMonth", this.selectedMonth);
      data.append("search", this.searchValue);

      if (this.selectedDepartment) {
        data.append("departmentId", this.selectedDepartment);
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
              this.days = res.data.data.days || [];
              this.summary = {
                ...this.summary,
                ...res.data.data.summary
              };

            } else {
              console.error("Error:", res.data.message);
              this.showToast(res.data.message || "Failed to load attendance data", "error");
            }
            this.loading = false;
          })
          .catch((e) => {
            console.error("API error:", e);
            this.showToast("Network error while loading attendance data", "error");
            this.loading = false;
          });
    },

    changePage(page) {
      if (page < 1 || page > this.summary.totalPages || page === this.currentPage) {
        return;
      }
      this.currentPage = page;
      this.getAttendanceData();
    },

    exportToExcel() {
      if (this.employees.length === 0) {
        this.showToast("No data to export", "warning");
        return;
      }

      try {
        const workbook = XLSX.utils.book_new();

        // Create headers
        const headers = ['Employee', 'Join Date'];
        this.days.forEach(day => {
          headers.push(this.formatDay(day));
        });

        // Create data
        const attendanceData = [];
        attendanceData.push(headers);

        this.employees.forEach(employee => {
          const row = [
            employee.employeeName,
            this.formatJoinDate(employee.dateJoined)
          ];
          this.days.forEach(day => {
            const status = employee.attendanceByDay[day];
            const displayStatus = this.getDisplayStatus(status, day);
            row.push(displayStatus || '-');
          });
          attendanceData.push(row);
        });

        // Add summary sheet
        const summaryData = [
          ['Monthly Attendance Summary'],
          ['Period', this.summary.period.month],
          ['Total Employees', this.summary.totalEmployees],
          ['Total Working Days', this.summary.totalWorkingDays],
          ['Total Present', this.summary.totalPresent],
          ['Total Absent', this.summary.totalAbsent],
          ['Total Leave', this.summary.totalLeave],
          ['Missing Data Days', this.summary.totalNoData],
          ['Weekend Work', this.summary.totalWeekendWork],
          ['Weekend Leave', this.summary.totalWeekendLeave]
        ];

        const attendanceSheet = XLSX.utils.aoa_to_sheet(attendanceData);
        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

        XLSX.utils.book_append_sheet(workbook, attendanceSheet, 'Attendance Report');
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

        const fileName = `Attendance_Report_${this.selectedMonth}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        this.showToast("Excel report downloaded successfully", "success");
      } catch (error) {
        console.error("Error generating Excel file:", error);
        this.showToast("Failed to generate Excel report", "error");
      }
    }
  },
  watch: {
    selectedDepartment() {
      this.currentPage = 1;
      this.getAttendanceData();
    }
  }
};
</script>

<style scoped>
.card {
  border-radius: 0.5rem;
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 500;
}

.font-size-24 {
  font-size: 1.5rem !important;
}

.font-size-48 {
  font-size: 3rem !important;
}

.font-size-15 {
  font-size: 0.9375rem !important;
}

.font-size-18 {
  font-size: 1.125rem !important;
}

.bg-light-warning {
  background-color: #fff8e8 !important;
}

.sticky-top {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 999;
}

.position-sticky.start-0 {
  position: sticky;
  left: 0;
  z-index: 99;
  background-color: #fff;
}

.employee-row:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.weekend-work {
  position: relative;
  animation: weekend-glow 2s ease-in-out infinite alternate;
}

.weekend-work::before {
  content: "⭐";
  position: absolute;
  top: -8px;
  right: -8px;
  font-size: 12px;
  z-index: 10;
}

.weekend-leave {
  border: 2px solid #6c757d;
}

.weekend-absent {
  border: 2px dashed #dc3545;
}

@keyframes weekend-glow {
  from {
    box-shadow: 0 0 5px rgba(13, 110, 253, 0.5);
  }
  to {
    box-shadow: 0 0 15px rgba(13, 110, 253, 0.8);
  }
}

.current-day {
  position: relative;
}

.current-day::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid #556ee6;
  pointer-events: none;
}

.pagination-rounded .page-link {
  border-radius: 50%;
  margin: 0 3px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-item.active .page-link {
  background-color: #556ee6;
  border-color: #556ee6;
}

.page-link {
  color: #556ee6;
}

.page-link:hover {
  color: #2b3a92;
  background-color: #f0f0f8;
}

.opacity-50 {
  opacity: 0.5 !important;
}

.bg-muted {
  background-color: #6c757d !important;
}

@media (max-width: 768px) {
  .table {
    font-size: 0.85rem;
  }

  .card-header .row {
    row-gap: 1rem;
  }
}

/* Enhanced visual feedback for data states */
.alert {
  border-left: 4px solid;
}

.alert-warning {
  border-left-color: #ffc107;
}

/* Better spacing for mobile */
@media (max-width: 576px) {
  .avatar-sm {
    width: 2rem;
    height: 2rem;
  }

  .font-size-24 {
    font-size: 1.25rem !important;
  }
}
</style>